import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {RouteService} from '../../../services/route/route.service';
import {CartService} from '../../../services/cart/cart.service';
import {MatMenuTrigger} from '@angular/material/menu';
import {StorageService} from '../../../services/storage/storage.service';
import moment from 'moment';
import {AuthService} from '../../../services/auth/auth.service';
import {FiscalService} from '../../../services/fiscal/fiscal.service';
import {ConfirmDialogComponent} from '../../../components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {UtilService} from '../../../services/util/util.service';
import {finalize} from 'rxjs/operators';
import {Privileges} from '../../../modules/admin/privileges/privileges.constants';
import {CartTypes} from '../../../services/cart/cart-base.service';
import {TicketTypeService} from '../../../services/ticket-type/ticket-type.service';
import {tap} from 'rxjs';

@Component({
  selector: 'app-cart-menu',
  templateUrl: './cart-menu.component.html',
  styleUrls: ['./cart-menu.component.scss']
})
export class CartMenuComponent implements OnInit {
  @ViewChild('cartMenuTrigger', {read: MatMenuTrigger}) public cartMenuTrigger: MatMenuTrigger;

  _caller = false;
  @Input()
  get caller() { return this._caller; }
  set caller(val: boolean) {
    this._caller = val;
    this.callerChange.emit(this._caller);
  }
  @Output()
  callerChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public Privileges = Privileges;
  public CartTypes = CartTypes;

  constructor(
    public authService: AuthService,
    public routeService: RouteService,
    public cartService: CartService,
    public fiscalService: FiscalService,
    public utilService: UtilService,
    public ticketTypeService: TicketTypeService,
    private dialog: MatDialog,
    private storageService: StorageService,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.storageService.changes.subscribe(({key, value}) => {
      if (this.caller) {
        this.caller = false;
        return;
      }
      if (key === CartService.STORAGE_CART_PREVIEW && this.cartMenuTrigger) {
        if (value) {
          this.cartMenuTrigger.openMenu();
        } else {
          this.cartMenuTrigger.closeMenu();
        }
      }
      if (key === CartService.STORAGE_CART_WINDOW_CLOSE) {
        if (this.routeService.isWindow) {
          window.close();
        }
      }
    });
  }

  public get isCart(): boolean {
    return this.routeService.checkRoute(['sales-terminal', 'cart']);
  }

  public get isDisabled(): boolean {
    return this.cartService.isEmpty || (this.authService.checkSalespointProperty('fiscal_procedures_by') && !this.fiscalService.isReady);
  }

  public get isOpen(): boolean {
    return this.cartMenuTrigger?.menuOpen;
  }

  public removeTicket(ticketId: number): void {
    this.cartService.isLoading = true;
    const ticketIds = {tickets: [ticketId]};
    this.cartService.removeCartTickets(ticketIds).pipe(
      finalize(() => this.cartService.isLoading = false)
    ).subscribe(cart => {
      this.updateCart();
      if (this.cartService.isEmpty) {
        this.storageService.set(CartService.STORAGE_CART_CLEAR, moment.now());
        this.cartMenuTrigger.closeMenu();
      }
    });
  }
  public removeTicketType(ticketTypeUuid: string, quantity: number, eventId: number): void {
    this.cartService.removeTicketTypes(eventId, [{ticketTypeUuid: ticketTypeUuid, quantity: (quantity*-1)}]);
  }

  private updateCart(): void {
    this.cartService.updateSelectedTicketsTotal();
  }

  public confirmClear(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_cart_clear_confirmation_title'),
        description: this.translateService.instant('general.api_cart_clear_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_cart_clear')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.cartService.emptyCart(() => {
          this.updateCart();
          this.cartMenuTrigger.closeMenu();
        });
      }
    });
  }

  public handleCartOpen(): void {
    if (this.cartService.isSelfService) {
      this.ticketTypeService.initTicketTypeHandling(this.cartService, this.cartService.events);
    }
    this.caller = true;
    this.storageService.set(CartService.STORAGE_CART_PREVIEW, moment.now());
  }
  public handleCartClose(): void {
    this.caller = true;
    this.storageService.set(CartService.STORAGE_CART_PREVIEW, 0);
  }

  public windowGoToCart(): void {
    this.storageService.set(CartService.STORAGE_CART_CHECKOUT, moment.now());
  }

  public get canCreateBookingFromMenu(): boolean {
    return this.authService.checkPrivileges(Privileges.BOOKING_CREATE) && !this.routeService.isWindow &&
      this.cartService.cartType !== CartTypes.SelfService && this.cartService.ticketCount && !this.isCart;
  }

  public changeTicketTypeCount(eventId: number, ticketTypeUuid: string, count?: number): void {
    this.ticketTypeService.changeTicketTypeCount(ticketTypeUuid, eventId, count);
  }

  public getTicketTypeMax(id: string, eventId: number): number {
    return this.cartService.calculateMaxTickets(this.ticketTypeService.getTicketTypeInitialCount(id) + this.ticketTypeService.getTicketTypeAvailable(id, eventId));
  }

  public removePromocode(eventId: number): void {
    this.cartService.removeSelfServicePromocode(eventId).pipe(
      tap({finalize: () => this.cartService.retryCart(false, eventId)})
    ).subscribe();
  }
}
